import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useSmartBack } from "../hooks/smartBack";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Filter20Regular,
  Search20Regular,
  Warning20Regular,
  Building20Regular,
  Person20Regular,
  PersonBoard20Regular,
  Save20Regular,
  Dismiss20Regular,
  CheckmarkCircle20Regular,
  ChevronRight20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { VerantwortlichkeitInput } from "../components/verantwortlichkeit";
import type { VerantwortlichkeitValue } from "../components/verantwortlichkeit";
import { ContactRefField } from "../components/relationsInline/ContactRefField";
import type { ContactRef } from "../components/relationsInline/types";
import { CleanupContactPreview } from "../components/CleanupContactPreview";
import { CleanupContactEditPanel } from "../components/CleanupContactEditPanel";
import { useAuth } from "../context/AuthContext";
import {
  CleanupMode,
  CleanupStatus,
  CleanupPersonDetail,
  CleanupCompanyRef,
  FirmaDetail,
  PersonDetail,
  PersonCleanupInput,
  listCleanup,
  listCleanupTeams,
  fetchCleanupFirma,
  fetchCleanupPerson,
  fetchKvCompaniesForPerson,
  KvCompany,
  saveCleanupBatch,
  deleteCleanupContacts,
} from "../services/ansprechpartnerCleanupService";
import { bestaetigen } from "../components/redesign/bestaetigen";

const PAGE_SIZE = 30;

// Geführter Frage-Flow (in dieser Reihenfolge):
//  1) „Gibt es den Kontakt noch?" (exists) → Nein: automatisch löschen
//     (Grund fix „Kontakt ist kein Ansprechpartner mehr", kein Grund-Feld) +
//     Verbindung löschen. Bei Status „Kunde" ist nur „Ja" möglich (Nein gesperrt).
//  2) „Hat der Kontakt eine Verantwortlichkeit fürs Unternehmen?" (hasVerant)
//       → Ja: intern/extern + Dropdown.
//       → Nein: die aktuelle Firma-Verbindung wird gelöscht + Frage „Ist der
//         Kontakt weiterhin im Unternehmen beschäftigt (Mitarbeiter/Mensch im
//         Unternehmen)?" (mitarbeiter) → Ja: Status „Mensch im Unternehmen".
/** Punkt 2: zusätzliche Firma aus der Kundenverbindung der Person + eigene Verantwortlichkeit. */
type KvAssignment = {
  companyId: number;
  companyName: string | null;
  verant: VerantwortlichkeitValue;
};
type PersonForm = {
  exists: boolean | null; // „Gibt es den Kontakt noch?" (true=existiert, false=weg→löschen)
  hasVerant: boolean | null; // „Hat der Kontakt eine Verantwortlichkeit fürs Unternehmen?"
  verant: VerantwortlichkeitValue; // { isExtern, roles[], externalCompany }
  mitarbeiter: boolean | null; // nur wenn hasVerant===false: weiterhin im Unternehmen?
  /** Punkt 2: zusätzlich gewählte KV-Firmen mit je eigener Verantwortlichkeit (by-firma). */
  kvAssignments: KvAssignment[];
};

// Fixer Lösch-Grund, wenn der Kontakt kein Ansprechpartner mehr ist (kein Feld).
const AUTO_DELETE_REASON = "Kontakt ist kein Ansprechpartner mehr";

// Punkt 3: Entwurf wird in localStorage gehalten → überlebt In-App-Navigation,
// Reload UND Tab schließen/öffnen (gleicher Browser/Gerät). Keine Warnung nötig,
// da nichts verloren geht.
const DRAFT_KEY = "kvp.apcleanup.draft";
type DraftDetail = {
  selectedId: number;
  mode: CleanupMode;
  form: Record<number, PersonForm>;
  added: Array<{ key: number; ref: ContactRef; verant: VerantwortlichkeitValue }>;
  addDraft: ContactRef | null;
  personExists: boolean | null;
};
type Draft = {
  v: 1;
  scope: string;
  list: { mode: CleanupMode; status: CleanupStatus; teamId: string; q: string };
  detail: DraftDetail | null;
};

const blankVerant = (): VerantwortlichkeitValue => ({ isExtern: false, roles: [], externalCompany: null });

// Leeres Formular — bewusst KEINE Vorauswahl (null/leer = unbeantwortet).
const blankForm = (): PersonForm => ({
  exists: null,
  hasVerant: null,
  verant: blankVerant(),
  mitarbeiter: null,
  kvAssignments: [],
});

/** Vorbelegung aus der bestehenden Beziehung (currentRelationType/Function/IsExtern/Share).
 *  statusLabel: bei „Kunde" ist „Gibt es den Kontakt noch?" auf Ja fixiert. */
const formFromRelation = (cur: {
  currentRelationType: string | null;
  currentFunction: string | null;
  currentIsExtern: boolean | null;
  currentShare: string | null;
  currentExternalCompanyId: number | null;
  currentExternalCompanyName: string | null;
  statusLabel?: string | null;
}): PersonForm => {
  const f = blankForm();
  // Hinweis: Bei Status „Kunde" wird „Nein" in der UI gesperrt (Kunde nicht löschbar);
  // `exists` bleibt aber null, damit unangetastete Personen NICHT mitgespeichert werden.
  const rt = cur.currentRelationType;
  if (rt === "Geschäftsführer") {
    f.hasVerant = true;
    f.verant = { isExtern: false, roles: [{ kind: "gf" }], externalCompany: null };
  } else if (rt === "Gesellschafter") {
    f.hasVerant = true;
    f.verant = {
      isExtern: false,
      roles: [{ kind: "gesellschafter", share: cur.currentShare ?? undefined, istGeschaeftsfuehrer: cur.currentFunction === "geschäftsführend" }],
      externalCompany: null,
    };
  } else if (rt === "Mensch fürs Unternehmen" && cur.currentFunction?.trim()) {
    f.hasVerant = true;
    // Bestehende externe Firma in den Picker vorbelegen (verhindert, dass ein
    // Re-Save ohne erneute Auswahl die Verknüpfung nullt).
    const ec: ContactRef | null =
      cur.currentIsExtern && cur.currentExternalCompanyId
        ? {
            contactId: cur.currentExternalCompanyId,
            type: "Firma",
            lastName: cur.currentExternalCompanyName ?? undefined,
            displayName: cur.currentExternalCompanyName ?? undefined,
          }
        : null;
    f.verant = {
      isExtern: !!cur.currentIsExtern,
      roles: [{ kind: "mensch", verantwortlichkeit: cur.currentFunction.trim(), share: cur.currentShare ?? undefined }],
      externalCompany: ec,
    };
  }
  return f;
};

// Farbcode für den bisherigen Kundenstatus (Kunde/Interessent/Altkunde/…).
const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  Kunde: { bg: "#e7f6ec", fg: "#1b5e20" },
  Interessent: { bg: "#f0e6f2", fg: "#4d2051" },
  Altkunde: { bg: "#fdf3d6", fg: "#8a6d00" },
  "Verantwortung fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  // Alter Name desselben Status (bis 29.09.2026).
  "Mensch fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  Haushaltsmitglied: { bg: "#e4f3f1", fg: "#13675a" },
};
const statusChipStyle = (s: string | null | undefined): React.CSSProperties => {
  const c = (s && STATUS_COLORS[s]) || { bg: "#ececec", fg: "#5b5560" };
  return { backgroundColor: c.bg, color: c.fg };
};
const statusLabelText = (s: string | null | undefined) =>
  `Status: ${s ?? "ohne Status"}`;

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1400px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    ...shorthands.padding("20px", "24px"),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  filterBar: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  pill: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ...shorthands.padding("6px", "12px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  pillActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    // Hover muss Purple behalten — sonst gewinnt pill:hover (heller bg) und die
    // weiße Schrift wird unlesbar (weiß auf hell).
    ":hover": { backgroundColor: BRAND.purple, color: BRAND.white },
  },
  spacer: { width: "1px", height: "20px", backgroundColor: BRAND.borderDashed, marginLeft: "4px", marginRight: "4px" },
  select: {
    marginLeft: "8px",
    ...shorthands.padding("4px", "8px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", BRAND.muted),
    fontSize: "0.8rem",
    backgroundColor: "#fff",
    color: BRAND.ink,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("6px", "12px"),
  },
  searchInput: {
    flexGrow: 1,
    appearance: "none",
    ...shorthands.border("0", "solid", "transparent"),
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    outlineStyle: "none",
  },
  card: {
    appearance: "none",
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    ...shorthands.padding("14px", "16px"),
    marginBottom: "10px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    ":hover": { ...shorthands.borderColor(BRAND.purple), backgroundColor: "#f5eef6" },
  },
  cardIcon: {
    width: "38px",
    height: "38px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
  },
  cardName: { fontFamily: BRAND.fontAccent, fontSize: "1rem", color: BRAND.ink, flexGrow: 1 },
  cardCol: { display: "flex", flexDirection: "column", rowGap: "2px", flexGrow: 1, minWidth: 0 },
  cardFirms: {
    fontSize: "0.82rem",
    color: BRAND.muted,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  countBadge: {
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  // Kontaktverantwortlichkeit (K-Verantwortlicher) in der Listenzeile.
  respChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: BRAND.purple,
    backgroundColor: "#f3eef5",
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px", "9px"),
    whiteSpace: "nowrap",
    maxWidth: "180px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  empty: {
    color: BRAND.muted,
    fontStyle: "italic",
    paddingTop: "20px",
    paddingBottom: "20px",
    textAlign: "center",
  },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  successBox: {
    backgroundColor: "#e7f6ec",
    color: "#1b5e20",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.border("1px", "solid", "#a5d6b7"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
  detailHead: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("14px", "18px"),
    boxShadow: "0 1px 2px rgba(45,20,43,0.04), 0 6px 20px rgba(45,20,43,0.05)",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  detailTitle: { fontFamily: BRAND.fontAccent, fontSize: "1.35rem", fontWeight: 700, color: BRAND.ink },
  detailMeta: { color: BRAND.muted, fontSize: "0.85rem" },
  personCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("14px", "18px"),
    marginBottom: "10px",
    boxShadow: "0 1px 2px rgba(45,20,43,0.04), 0 6px 20px rgba(45,20,43,0.05)",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  personHeadRow: { display: "flex", alignItems: "center", columnGap: "10px", flexWrap: "wrap" },
  personName: { fontFamily: BRAND.fontAccent, fontSize: "1.2rem", fontWeight: 700, color: BRAND.ink },
  statusInlineLabel: { fontSize: "0.8rem", fontWeight: 600, color: BRAND.muted },
  statusChip: {
    fontSize: "0.72rem",
    fontWeight: 700,
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("1px", "8px"),
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
  },
  bereinigtChip: {
    fontSize: "0.72rem",
    fontWeight: 700,
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("1px", "8px"),
    backgroundColor: "#e7f6ec",
    color: "#1b5e20",
  },
  // Prominenter „Bisher: Rolle · Funktion"-Chip (deutlicher als muted-Text).
  currentRoleChip: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding("2px", "10px"),
  },
  // Hintergrund-Infos gedämpft: graues Label + dunkler Wert (Name bleibt das schwere Element).
  infoBlock: { display: "flex", flexDirection: "column", rowGap: "3px", marginTop: "2px" },
  infoLine: { display: "flex", columnGap: "6px", flexWrap: "wrap", alignItems: "baseline", fontSize: "0.85rem" },
  infoLabel: { fontWeight: 600, color: BRAND.muted },
  infoValue: { fontWeight: 600, color: BRAND.ink },
  // Eyebrow: kleiner, gesperrter Gruppen-Titel über jeder Frage (deep aubergine).
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "5px",
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: BRAND.purple,
    marginBottom: "1px",
  },
  eyebrowDanger: { color: "#b3261e" },
  // Frageblock mit feiner Trennlinie darüber → klare, luftige Schritte.
  qBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "9px",
    paddingTop: "12px",
    marginTop: "12px",
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
  },
  qLabel: { fontWeight: 600, fontSize: "0.96rem", color: BRAND.ink, lineHeight: 1.4 },
  qHint: { fontSize: "0.82rem", color: BRAND.muted, lineHeight: 1.45 },
  // Ja/Nein-Buttons (statt Checkbox).
  yesNoWrap: { display: "flex", columnGap: "8px", flexWrap: "wrap" },
  yesNoBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontWeight: 600,
    fontSize: "0.88rem",
    ...shorthands.padding("7px", "18px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("8px"),
    transitionProperty: "border-color, background-color, color",
    transitionDuration: "120ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple), color: BRAND.purple },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  yesNoActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.purple, color: BRAND.white },
  },
  yesNoActiveDanger: {
    backgroundColor: "#b3261e",
    color: BRAND.white,
    ...shorthands.borderColor("#b3261e"),
    ":hover": { backgroundColor: "#b3261e" },
  },
  // Rot umrandeter Lösch-Bereich.
  dangerBox: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    ...shorthands.padding("12px", "14px"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#fdecea",
    ...shorthands.border("1px", "solid", "#f3b4ad"),
    marginTop: "4px",
  },
  reasonInput: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#f3b4ad"),
    outlineStyle: "none",
  },
  formRow: { display: "flex", alignItems: "center", columnGap: "12px", flexWrap: "wrap" },
  formLabel: { fontSize: "0.82rem", fontWeight: 600, color: BRAND.ink, minWidth: "150px" },
  roleWrap: { display: "flex", columnGap: "6px", flexWrap: "wrap" },
  roleBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ...shorthands.padding("5px", "10px"),
    fontSize: "0.8rem",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  roleBtnActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.purple, color: BRAND.white },
  },
  check: { display: "inline-flex", alignItems: "center", columnGap: "6px", fontSize: "0.85rem", cursor: "pointer" },
  subForm: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    ...shorthands.padding("10px", "12px"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  infoToggle: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#fff",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    ":hover": { backgroundColor: BRAND.bg },
  },
  infoToggleChevron: { transitionProperty: "transform", transitionDuration: "120ms" },
  infoToggleChevronOpen: { transform: "rotate(90deg)" },
  infoPanel: { ...shorthands.padding("4px", "0", "0", "0") },
  fieldLabel: { display: "flex", flexDirection: "column", rowGap: "3px", maxWidth: "320px" },
  fieldLabelText: {
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: BRAND.muted,
  },
  textInput: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    outlineStyle: "none",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
    paddingTop: "8px",
  },
  btn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  btnPrimary: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.ink },
  },
  // Punkt 2: KV-Firmen-Block
  kvPanel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    marginTop: "6px",
    paddingLeft: "8px",
    ...shorthands.borderLeft("2px", "solid", BRAND.borderDashed),
  },
  kvList: { display: "flex", flexDirection: "column", rowGap: "6px" },
  kvOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    fontSize: "0.88rem",
    color: BRAND.ink,
    cursor: "pointer",
  },
  kvAssignCard: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    backgroundColor: BRAND.bg,
    ...shorthands.padding("10px", "12px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  kvAssignName: { fontFamily: BRAND.fontAccent, fontWeight: 700, color: BRAND.ink },
});

const personName = (lastname: string | null, firstName: string | null, id: number) =>
  [lastname, firstName].filter(Boolean).join(", ") || `#${id}`;

const AnsprechpartnerCleanupPage = () => {
  const styles = useStyles();
  const goBack = useSmartBack("/datenbereinigung");
  const { teams: myTeams, user } = useAuth();
  // Punkt 3: Entwurf-Persistenz (localStorage) — pro Benutzer gescoped, damit auf
  // einem geteilten Browser keine fremden Entwürfe auftauchen.
  const draftScope = String(user?.id ?? "anon");

  const [mode, setMode] = useState<CleanupMode>("by-firma");
  const [status, setStatus] = useState<CleanupStatus>("open");
  const [teamId, setTeamId] = useState<string>("");
  const [teams, setTeams] = useState<Array<{ id: string; name: string | null }>>([]);
  const teamDefaultedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const [q, setQ] = useState(initialQ);
  const [qDebounced, setQDebounced] = useState(initialQ.trim());

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Detail state.
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [firmaDetail, setFirmaDetail] = useState<FirmaDetail | null>(null);
  const [personDetail, setPersonDetail] = useState<PersonDetail | null>(null);
  const [form, setForm] = useState<Record<number, PersonForm>>({});
  // #4: Personen als „Mensch fürs Unternehmen" zur aktuellen Firma hinzufügen.
  const [addDraft, setAddDraft] = useState<ContactRef | null>(null);
  const [added, setAdded] = useState<
    Array<{ key: number; ref: ContactRef; verant: VerantwortlichkeitValue }>
  >([]);
  const addKeyRef = useRef(0);
  // Punkt 3: true, sobald der initiale localStorage-Restore gelaufen ist (erst
  // danach persistieren, sonst überschreibt der erste Render den Entwurf mit Leer).
  const restoredRef = useRef(false);
  // Ausklapp „Bisherige Informationen" je Kontakt-Id (lazy, eigener Speichern-Button).
  const [infoOpen, setInfoOpen] = useState<Record<number, boolean>>({});
  const toggleInfo = (cid: number) => setInfoOpen((prev) => ({ ...prev, [cid]: !prev[cid] }));
  // by-person: „Gibt es den Kontakt noch?" ist personen-global (Kopf).
  const [personExists, setPersonExists] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveDone, setSaveDone] = useState(false);

  // Punkt 2: KV-Firmen je Person (lazy geladen) + welche Person-Card ihren
  // „Weitere Firmen aus der Kundenverbindung"-Block aufgeklappt hat (per relationId).
  const [kvCompaniesByPerson, setKvCompaniesByPerson] = useState<
    Record<number, { loading: boolean; error: string | null; companies: KvCompany[] }>
  >({});
  const [kvOpen, setKvOpen] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    listCleanupTeams().then(setTeams).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!teamDefaultedRef.current && myTeams.length > 0) {
      teamDefaultedRef.current = true;
      setTeamId(myTeams[0].id);
    }
  }, [myTeams]);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const { rows: data, total: t } = await listCleanup(
        mode,
        status,
        teamId || undefined,
        qDebounced || undefined,
        PAGE_SIZE,
        0,
        controller.signal,
      );
      setRows(data);
      setTotal(t);
      setHasMore(data.length < t);
    } catch (e: any) {
      if (e?.name !== "AbortError") setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [mode, status, teamId, qDebounced]);

  useEffect(() => {
    if (selectedId == null) load();
    return () => abortRef.current?.abort();
  }, [load, selectedId]);

  const fetchMore = async () => {
    try {
      const { rows: data, total: t } = await listCleanup(
        mode,
        status,
        teamId || undefined,
        qDebounced || undefined,
        PAGE_SIZE,
        rows.length,
      );
      setRows((prev) => [...prev, ...data]);
      setTotal(t);
      setHasMore(rows.length + data.length < t);
    } catch (e: any) {
      setError(String(e?.message ?? e));
      setHasMore(false);
    }
  };

  const initFormFromFirma = (d: FirmaDetail) => {
    const f: Record<number, PersonForm> = {};
    for (const p of d.persons) f[p.relationId] = formFromRelation(p);
    setForm(f);
  };

  const initFormFromPerson = (d: PersonDetail) => {
    const f: Record<number, PersonForm> = {};
    for (const c of d.companies) f[c.relationId] = formFromRelation(c);
    setForm(f);
    // Bei „Kunde" wird „Nein" in der UI gesperrt; personExists bleibt null bis
    // der Bearbeiter es beantwortet.
    setPersonExists(null);
  };

  const openDetail = async (id: number, restore?: DraftDetail) => {
    const m = restore?.mode ?? mode;
    if (restore?.mode && restore.mode !== mode) setMode(restore.mode);
    setSelectedId(id);
    setDetailLoading(true);
    setFirmaDetail(null);
    setPersonDetail(null);
    setSaveError(null);
    setSaveDone(false);
    try {
      if (m === "by-firma") {
        const d = await fetchCleanupFirma(id);
        setFirmaDetail(d);
        if (restore) {
          // Punkt 3: Entwurf gegen frische Daten abgleichen — nur Relationen behalten,
          // die es noch gibt; neu hinzugekommene Personen blank-aus-Beziehung initialisieren.
          const f: Record<number, PersonForm> = {};
          for (const p of d.persons) f[p.relationId] = restore.form?.[p.relationId] ?? formFromRelation(p);
          setForm(f);
          setAdded(restore.added ?? []);
          setAddDraft(restore.addDraft ?? null);
        } else {
          initFormFromFirma(d);
        }
      } else {
        const d = await fetchCleanupPerson(id);
        setPersonDetail(d);
        if (restore) {
          const f: Record<number, PersonForm> = {};
          for (const c of d.companies) f[c.relationId] = restore.form?.[c.relationId] ?? formFromRelation(c);
          setForm(f);
          setPersonExists(restore.personExists ?? null);
        } else {
          initFormFromPerson(d);
        }
      }
    } catch (e: any) {
      setSaveError(String(e?.message ?? e));
    } finally {
      setDetailLoading(false);
    }
  };

  // Punkt 3: Entwurf verwerfen — auf den frisch geladenen Server-Stand zurücksetzen.
  const discardDraft = () => {
    if (mode === "by-firma" && firmaDetail) initFormFromFirma(firmaDetail);
    else if (mode === "by-person" && personDetail) initFormFromPerson(personDetail);
    setAdded([]);
    setAddDraft(null);
    setSaveError(null);
  };

  // Punkt 3: einmaliger Restore aus localStorage beim Mount (überlebt In-App-Nav,
  // Reload UND Tab schließen/öffnen). Reopent ggf. die zuletzt offene Detailansicht.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw) as Draft;
        if (d && d.scope === draftScope) {
          if (d.list) {
            setMode(d.list.mode);
            setStatus(d.list.status);
            setTeamId(d.list.teamId);
            teamDefaultedRef.current = true; // Auto-Team-Default nicht überschreiben
            setQ(d.list.q);
            setQDebounced((d.list.q ?? "").trim());
          }
          if (d.detail && d.detail.selectedId) {
            const maxKey = (d.detail.added ?? []).reduce((mx, a) => Math.max(mx, a.key), 0);
            addKeyRef.current = Math.max(addKeyRef.current, maxKey + 1);
            void openDetail(d.detail.selectedId, d.detail);
          }
        }
      }
    } catch {
      /* defekter Entwurf → ignorieren */
    }
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Punkt 3: Entwurf fortlaufend persistieren (erst NACH dem initialen Restore).
  // Beim Speichern setzt closeDetail selectedId=null → detail wird hier auf null
  // geschrieben (Entwurf-Detail geleert), die Listen-Filter bleiben erhalten.
  useEffect(() => {
    if (!restoredRef.current) return;
    const draft: Draft = {
      v: 1,
      scope: draftScope,
      list: { mode, status, teamId, q },
      detail:
        selectedId != null
          ? { selectedId, mode, form, added, addDraft, personExists }
          : null,
    };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      /* z. B. Quota → ignorieren */
    }
  }, [draftScope, mode, status, teamId, q, selectedId, form, added, addDraft, personExists]);

  const closeDetail = () => {
    setSelectedId(null);
    setFirmaDetail(null);
    setPersonDetail(null);
    setForm({});
    setAddDraft(null);
    setAdded([]);
    setSaveDone(false);
    setSaveError(null);
    setKvCompaniesByPerson({});
    setKvOpen({});
  };

  const setF = (relId: number, patch: Partial<PersonForm>) =>
    setForm((prev) => ({ ...prev, [relId]: { ...prev[relId], ...patch } }));

  // Punkt 2: KV-Zuordnungen einer Person-Card (per relationId) ändern.
  const setKvAssignments = (
    relId: number,
    updater: (prev: KvAssignment[]) => KvAssignment[],
  ) =>
    setForm((prev) => ({
      ...prev,
      [relId]: { ...prev[relId], kvAssignments: updater(prev[relId]?.kvAssignments ?? []) },
    }));

  // KV-Firmen der Person lazy laden (einmal pro Person, gecacht).
  const loadKvCompanies = async (personId: number, excludeCompanyId: number) => {
    if (kvCompaniesByPerson[personId]) return; // schon geladen/lädt
    setKvCompaniesByPerson((prev) => ({
      ...prev,
      [personId]: { loading: true, error: null, companies: [] },
    }));
    try {
      const companies = await fetchKvCompaniesForPerson(personId, excludeCompanyId);
      setKvCompaniesByPerson((prev) => ({
        ...prev,
        [personId]: { loading: false, error: null, companies },
      }));
    } catch (e: any) {
      setKvCompaniesByPerson((prev) => ({
        ...prev,
        [personId]: { loading: false, error: String(e?.message ?? e), companies: [] },
      }));
    }
  };

  // Validierung der Frage-Kette einer by-firma-Person-Card (inkl. Existenz):
  // unangetastet (exists===null) wird übersprungen; gestartet-aber-unvollständig blockt.
  const relUnanswered = (fr?: PersonForm): boolean => {
    if (!fr) return false;
    if (fr.exists === null) return false; // nicht angefasst
    if (fr.exists === false) return false; // löschen
    if (fr.hasVerant === null) return true; // Verantwortlichkeit-Frage offen
    if (fr.hasVerant === true) return fr.verant.roles.length === 0; // Ja → Rolle nötig
    return fr.mitarbeiter === null; // hasVerant===false → Mitarbeiter-Frage offen
  };
  // by-person: Existenz ist personen-global (personExists); ist sie „Ja", muss
  // JEDE Firma-Beziehung beantwortet sein.
  const companyUnanswered = (fr?: PersonForm): boolean => {
    if (!fr) return true;
    if (fr.hasVerant === null) return true;
    if (fr.hasVerant === true) return fr.verant.roles.length === 0;
    return fr.mitarbeiter === null;
  };

  // Baut Save-Items je Beziehung. Verantwortlichkeit=Ja → Rolle(n) (erste Rolle
  // aktualisiert relationId, weitere isNew). Verantwortlichkeit=Nein → Beziehung
  // löschen (+ Status „Mensch im Unternehmen", wenn weiterhin im Unternehmen).
  const buildItems = (
    relationId: number,
    personId: number,
    companyId: number,
    fr: PersonForm | undefined,
  ): PersonCleanupInput[] => {
    if (!fr) return [];
    if (fr.hasVerant === false) {
      return [{
        relationId,
        personId,
        companyId,
        isMitarbeiterBav: false,
        deleteRelation: true,
        isMenschImUnternehmen: fr.mitarbeiter === true,
      }];
    }
    if (fr.hasVerant !== true) return []; // unbeantwortet → Validierung blockt
    const v = fr.verant;
    if (!v.roles.length) return []; // Ja ohne Rolle → Validierung blockt

    // Externe Firma (eine, nur bei extern) → bestehende Id ODER Neu-Anlage.
    const ext = v.isExtern ? v.externalCompany : null;
    const externalCompanyId = ext?.contactId;
    const externalCompanyNew =
      !externalCompanyId && ext?.lastName?.trim()
        ? {
            name: ext.lastName.trim(),
            phone: ext.phone,
            email: ext.email,
            web: ext.web,
            branche: ext.branche,
            legalFormId: ext.legalFormId,
          }
        : undefined;

    return v.roles.map((r, idx): PersonCleanupInput => {
      const base = {
        relationId,
        personId,
        companyId,
        isMitarbeiterBav: false,
        isNew: idx > 0,
      };
      if (r.kind === "gf") return { ...base, role: "gf" };
      if (r.kind === "gesellschafter")
        return { ...base, role: "gesellschafter", share: r.share, istGeschaeftsfuehrer: r.istGeschaeftsfuehrer };
      return {
        ...base,
        role: "mensch",
        isExtern: v.isExtern,
        verantwortlichkeit: r.verantwortlichkeit,
        // Auch eine Nicht-Gesellschafter-Rolle kann eine Quote tragen
        // (Kommanditist, Wohnungseigentümer, Aktionär) — siehe verantToItems.
        share: r.share,
        externalCompanyId: v.isExtern ? externalCompanyId : undefined,
        externalCompanyNew: v.isExtern ? externalCompanyNew : undefined,
      };
    });
  };

  // #4: hinzugefügte Personen → „Mensch fürs Unternehmen"-Items (isNew + newPerson-Ref).
  const buildAddedItems = (companyId: number): PersonCleanupInput[] =>
    added.flatMap((a) => {
      const ref = a.ref;
      const hasRef = !!(ref.contactId || ref.lastName?.trim());
      if (!hasRef || !a.verant.roles.length) return [];
      const pseudo: PersonForm = {
        exists: true,
        hasVerant: true,
        verant: a.verant,
        mitarbeiter: null,
        kvAssignments: [],
      };
      const newPerson = ref.contactId
        ? { contactId: ref.contactId }
        : {
            lastName: ref.lastName?.trim(),
            firstName: ref.firstName?.trim(),
            gender: ref.gender,
            salutationId: ref.salutationId,
            salutationLabel: ref.salutationLabel,
            birthDate: ref.birthDate,
          };
      return buildItems(0, ref.contactId ?? 0, companyId, pseudo).map((it) => ({
        ...it,
        isNew: true,
        newPerson,
      }));
    });

  // Punkt 2: zusätzliche KV-Firmen einer Person → je eigene Verantwortlichkeit als
  // NEUE Relation (isNew, keine bestehende Beziehung Person↔KV-Firma). Spiegelt
  // buildAddedItems, aber bestehende Person (personId) statt newPerson.
  const buildKvItems = (personId: number, fr: PersonForm): PersonCleanupInput[] =>
    (fr.kvAssignments ?? []).flatMap((ka) => {
      if (!ka.verant.roles.length) return [];
      const pseudo: PersonForm = {
        exists: true,
        hasVerant: true,
        verant: ka.verant,
        mitarbeiter: null,
        kvAssignments: [],
      };
      return buildItems(0, personId, ka.companyId, pseudo).map((it) => ({
        ...it,
        isNew: true,
      }));
    });

  const onSave = async () => {
    let items: PersonCleanupInput[] = [];
    // Zu löschende Personen (Kontakt gibt es nicht mehr → Papierkorb mit Grund).
    const deletes: Array<{ personId: number; reason: string; name: string }> = [];

    if (mode === "by-firma" && firmaDetail) {
      const companyId = firmaDetail.firma.id;
      for (const p of firmaDetail.persons) {
        const fr = form[p.relationId];
        if (fr?.exists === false) {
          deletes.push({
            personId: p.personId,
            reason: AUTO_DELETE_REASON,
            name: personName(p.lastname, p.firstName, p.personId),
          });
          continue; // gelöschte Person nicht zusätzlich bereinigen
        }
        // NUR tatsächlich bearbeitete Personen speichern (exists===true). Unangetastete
        // (exists===null) überspringen — sonst würden vorbelegte Personen mitgespeichert.
        if (fr?.exists === true) {
          items.push(...buildItems(p.relationId, p.personId, companyId, fr));
          // Punkt 2: zusätzliche KV-Firmen-Verantwortlichkeiten der Person.
          items.push(...buildKvItems(p.personId, fr));
        }
      }
      // #4: hinzugefügte Personen (Mensch fürs Unternehmen) anhängen.
      items.push(...buildAddedItems(companyId));
    } else if (mode === "by-person" && personDetail) {
      const personId = personDetail.person.id;
      if (personExists === false) {
        deletes.push({
          personId,
          reason: AUTO_DELETE_REASON,
          name: personName(personDetail.person.lastname, personDetail.person.firstName, personId),
        });
      } else {
        items = personDetail.companies.flatMap((c) =>
          buildItems(c.relationId, personId, c.companyId, form[c.relationId]),
        );
      }
    }

    // Jede sichtbare Frage muss beantwortet sein (unbeantwortet blockt).
    const unanswered =
      mode === "by-firma"
        ? (firmaDetail?.persons ?? []).some((p) => relUnanswered(form[p.relationId]))
        : personExists === null ||
          (personExists === true &&
            (personDetail?.companies ?? []).some((c) => companyUnanswered(form[c.relationId])));
    if (unanswered) {
      setSaveError(
        "Bitte alle Fragen beantworten: Gibt es den Kontakt noch? / Verantwortlichkeit fürs Unternehmen? (+ Rolle bzw. Mitarbeiter-Frage).",
      );
      return;
    }

    // #4: hinzugefügte Person ohne gewählte Verantwortlichkeit blockt.
    if (
      added.some(
        (a) =>
          (a.ref.contactId || a.ref.lastName?.trim()) && !a.verant.roles.length,
      )
    ) {
      setSaveError(
        "Bitte für jede hinzugefügte Person eine Verantwortlichkeit wählen.",
      );
      return;
    }

    // Punkt 2: gewählte KV-Firma ohne Verantwortlichkeit blockt.
    if (
      mode === "by-firma" &&
      (firmaDetail?.persons ?? []).some((p) => {
        const fr = form[p.relationId];
        return (
          fr?.exists === true &&
          (fr.kvAssignments ?? []).some((ka) => !ka.verant.roles.length)
        );
      })
    ) {
      setSaveError(
        "Bitte für jede zusätzlich gewählte Firma aus der Kundenverbindung eine Verantwortlichkeit wählen.",
      );
      return;
    }

    if (items.length === 0 && deletes.length === 0) {
      setSaveError(
        "Bitte mindestens eine Angabe machen (Existenz beantworten oder Verantwortlichkeit wählen).",
      );
      return;
    }

    // Neue externe Firma: dieselben Pflichtfelder wie die Firmen-Anlage erzwingen
    // (Firmenname ist bereits durch das Eintippen gesetzt).
    const incomplete = items.find(
      (it) =>
        it.externalCompanyNew &&
        (!it.externalCompanyNew.branche?.trim() ||
          !it.externalCompanyNew.legalFormId ||
          !it.externalCompanyNew.phone?.trim() ||
          !it.externalCompanyNew.email?.trim()),
    );
    if (incomplete?.externalCompanyNew) {
      setSaveError(
        `Neue externe Firma „${incomplete.externalCompanyNew.name}": Branche, Rechtsform (aus der Liste), Telefon und E-Mail sind Pflicht.`,
      );
      return;
    }

    // Löschen ist destruktiv (auch über den automatischen „Ja"-Vorschlag bei
    // Kontakten ohne Rolle/Status) → vor dem Verschieben in den Papierkorb die
    // betroffenen Kontakte bestätigen lassen.
    if (deletes.length > 0) {
      const liste = deletes.map((d) => `• ${d.name}`).join("\n");
      const ok = await bestaetigen({
        titel: deletes.length === 1 ? "Kontakt in den Papierkorb?" : `${deletes.length} Kontakte in den Papierkorb?`,
        text: `Diese Kontakte werden in den Papierkorb verschoben und lassen sich dort wiederherstellen:\n\n${liste}`,
        ja: "In den Papierkorb",
        ton: "danger",
      });
      if (!ok) return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      if (items.length > 0) await saveCleanupBatch(items);
      // Löschen über den cleanup-eigenen, tenant-validierten Endpoint (Scope
      // contacts:update — nicht der admin/gfgl-gegatete DELETE /contact). Verschiebt
      // die Kontakte in den Papierkorb UND entfernt die Firma↔Person-Beziehungen.
      if (deletes.length > 0)
        await deleteCleanupContacts(
          deletes.map((d) => d.personId),
          AUTO_DELETE_REASON,
        );
      setSaveDone(true);
      setTimeout(() => closeDetail(), 1200); // success → back to list, mode bleibt
    } catch (e: any) {
      setSaveError(String(e?.message ?? e)); // Fehler → bleiben
    } finally {
      setSaving(false);
    }
  };

  // Ja/Nein-Buttons (statt Checkbox). value null = unbeantwortet.
  const yesNo = (
    value: boolean | null,
    onChange: (v: boolean) => void,
    opts?: { danger?: boolean; disabled?: boolean; disableNo?: boolean },
  ) => {
    const activeCls = opts?.danger ? styles.yesNoActiveDanger : styles.yesNoActive;
    return (
      <div className={styles.yesNoWrap}>
        <button
          type="button"
          disabled={opts?.disabled}
          className={mergeClasses(styles.yesNoBtn, value === true && activeCls)}
          onClick={() => onChange(true)}
        >
          Ja
        </button>
        <button
          type="button"
          disabled={opts?.disabled || opts?.disableNo}
          className={mergeClasses(styles.yesNoBtn, (value === false) && styles.yesNoActive)}
          onClick={() => onChange(false)}
        >
          Nein
        </button>
      </div>
    );
  };

  // Punkt 2: „Weitere Firmen aus der Kundenverbindung" — zusätzliche Firmen wählen
  // und je Firma eine eigene Verantwortlichkeit vergeben. Lazy geladen beim Aufklappen.
  const renderKvBlock = (relId: number, personId: number, companyId: number) => {
    const fr = form[relId];
    if (!fr) return null;
    const open = !!kvOpen[relId];
    const kv = kvCompaniesByPerson[personId];
    const assignments = fr.kvAssignments ?? [];
    const selectedIds = new Set(assignments.map((a) => a.companyId));

    const toggleOpen = () => {
      const next = !open;
      setKvOpen((prev) => ({ ...prev, [relId]: next }));
      if (next) loadKvCompanies(personId, companyId);
    };
    const toggleCompany = (c: KvCompany, on: boolean) =>
      setKvAssignments(relId, (prev) =>
        on
          ? prev.some((a) => a.companyId === c.id)
            ? prev
            : [...prev, { companyId: c.id, companyName: c.name, verant: blankVerant() }]
          : prev.filter((a) => a.companyId !== c.id),
      );
    const setAssignVerant = (cid: number, v: VerantwortlichkeitValue) =>
      setKvAssignments(relId, (prev) =>
        prev.map((a) => (a.companyId === cid ? { ...a, verant: v } : a)),
      );

    return (
      <div className={styles.subForm}>
        <button
          type="button"
          className={styles.infoToggle}
          onClick={toggleOpen}
          aria-expanded={open}
        >
          <ChevronRight20Regular
            className={mergeClasses(styles.infoToggleChevron, open && styles.infoToggleChevronOpen)}
          />
          Weitere Firmen aus der Kundenverbindung
          {assignments.length > 0 ? ` (${assignments.length})` : ""}
        </button>
        {open && (
          <div className={styles.kvPanel}>
            {kv?.loading && <span className={styles.qHint}>Lade Firmen…</span>}
            {kv?.error && <span className={styles.qHint}>Fehler: {kv.error}</span>}
            {kv && !kv.loading && !kv.error && kv.companies.length === 0 && (
              <span className={styles.qHint}>Keine weiteren Firmen in der Kundenverbindung.</span>
            )}
            {kv && kv.companies.length > 0 && (
              <div className={styles.kvList}>
                {kv.companies.map((c) => (
                  <label key={c.id} className={styles.kvOption}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={(e) => toggleCompany(c, e.target.checked)}
                    />
                    <span>{c.name ?? `#${c.id}`}</span>
                    {c.statusLabel && (
                      <span className={styles.statusChip} style={statusChipStyle(c.statusLabel)}>
                        {c.statusLabel}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
            {assignments.map((a) => (
              <div key={a.companyId} className={styles.kvAssignCard}>
                <span className={styles.kvAssignName}>{a.companyName ?? `#${a.companyId}`}</span>
                <VerantwortlichkeitInput
                  idBase={`ap-${relId}-kv-${a.companyId}`}
                  value={a.verant}
                  onChange={(v) => setAssignVerant(a.companyId, v)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Rolle der Person FÜR DIE FIRMA (genau eine): GF/Gesellschafter ODER Ansprechpartner.
  // showKv (nur by-firma): zusätzlich KV-Firmen-Verantwortlichkeiten anbieten.
  const renderRoleQuestions = (
    relId: number,
    personId: number,
    companyId: number,
    showKv: boolean,
  ) => {
    const fr = form[relId];
    if (!fr) return null;
    return (
      <div className={styles.qBlock}>
        <span className={styles.qLabel}>Hat der Kontakt eine Verantwortlichkeit fürs Unternehmen?</span>
        {yesNo(fr.hasVerant, (v) => setF(relId, { hasVerant: v }))}

        {fr.hasVerant === true && (
          <div className={styles.subForm}>
            <span className={styles.qHint}>Mehrfachauswahl möglich.</span>
            <VerantwortlichkeitInput
              idBase={`ap-${relId}`}
              value={fr.verant}
              onChange={(v) => setF(relId, { verant: v })}
            />
            {showKv && renderKvBlock(relId, personId, companyId)}
          </div>
        )}

        {/* Keine Verantwortlichkeit → Verbindung löschen + Mitarbeiter-Frage */}
        {fr.hasVerant === false && (
          <div className={styles.qBlock} style={{ marginTop: 4 }}>
            <span className={styles.qLabel}>
              Ist der Kontakt weiterhin im Unternehmen beschäftigt (Mitarbeiter/Mensch im Unternehmen)?
            </span>
            <span className={styles.qHint}>Bei „Ja" wird der Status „Mensch im Unternehmen" gesetzt.</span>
            {yesNo(fr.mitarbeiter, (v) => setF(relId, { mitarbeiter: v }))}
          </div>
        )}
      </div>
    );
  };

  // Frage „Gibt es den Kontakt noch?" (zuerst). „Nein" → Papierkorb (Grund fix,
  // kein Eingabefeld) + Verbindung löschen. Bei Status „Kunde" ist „Nein" gesperrt.
  const renderExistsQ = (
    exists: boolean | null,
    onChange: (v: boolean) => void,
    isKunde: boolean,
  ) => (
    <div className={styles.qBlock}>
      <span className={styles.qLabel}>Gibt es den Kontakt noch?</span>
      {yesNo(exists, onChange, { disableNo: isKunde })}
      {isKunde && (
        <span className={styles.qHint}>Status „Kunde" — kann nicht gelöscht werden.</span>
      )}
      {exists === false && (
        <div className={styles.dangerBox}>
          <span className={mergeClasses(styles.eyebrow, styles.eyebrowDanger)}>⚠ Kontakt löschen</span>
          <span className={styles.qHint}>
            Der Kontakt wird in den Papierkorb verschoben (Grund: „{AUTO_DELETE_REASON}“).
          </span>
        </div>
      )}
    </div>
  );

  // Ausklapp „Bisherige Informationen" — alle Stammdaten editierbar (eigener
  // Speichern-Button), lazy beim Aufklappen, ohne Verbindungen/Verknüpfungen.
  const renderInfoExpander = (contactId: number) => {
    const open = !!infoOpen[contactId];
    return (
      <div style={{ display: "flex", flexDirection: "column", rowGap: 8 }}>
        <button
          type="button"
          className={styles.infoToggle}
          onClick={() => toggleInfo(contactId)}
          aria-expanded={open}
        >
          <ChevronRight20Regular
            className={mergeClasses(styles.infoToggleChevron, open && styles.infoToggleChevronOpen)}
          />
          Bisherige Informationen
        </button>
        {open && (
          <div className={styles.infoPanel}>
            <CleanupContactEditPanel contactId={contactId} />
          </div>
        )}
      </div>
    );
  };

  // --- Detail render ---
  if (selectedId != null) {
    return (
      <div className={styles.root}>
        <button className={styles.backBtn} onClick={closeDetail}>
          <ArrowLeft20Regular /> Zurück zur Liste
        </button>

        {detailLoading ? (
          <p className={styles.empty}>Lade Details…</p>
        ) : firmaDetail ? (
          <>
            <div className={styles.detailHead}>
              <div style={{ display: "flex", alignItems: "center", columnGap: 10, flexWrap: "wrap" }}>
                <span className={styles.detailTitle}>{firmaDetail.firma.name ?? `#${firmaDetail.firma.id}`}</span>
                <span className={styles.statusChip} style={statusChipStyle(firmaDetail.firma.statusLabel)}>
                  {statusLabelText(firmaDetail.firma.statusLabel)}
                </span>
              </div>
              {firmaDetail.firma.address && (
                <span className={styles.detailMeta}>
                  {[firmaDetail.firma.address.street, firmaDetail.firma.address.postalCode, firmaDetail.firma.address.city]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              )}
              {firmaDetail.firma.communications.length > 0 && (
                <span className={styles.detailMeta}>
                  {firmaDetail.firma.communications
                    .slice(0, 4)
                    .map((c) => c.value)
                    .join(" · ")}
                </span>
              )}
            </div>

            {firmaDetail.persons.map((p: CleanupPersonDetail) => {
              const fr = form[p.relationId];
              const nm = personName(p.lastname, p.firstName, p.personId);
              return (
                <div key={p.relationId} className={styles.personCard}>
                  <div className={styles.personHeadRow}>
                    <span className={styles.personName}>{nm}</span>
                    <span className={styles.statusInlineLabel}>Kontaktstatus:</span>
                    <span className={styles.statusChip} style={statusChipStyle(p.statusLabel)}>
                      {p.statusLabel ?? "ohne Status"}
                    </span>
                    {(p.extraStatuses ?? []).map((es) => (
                      <span key={es} className={styles.statusChip} style={statusChipStyle(es)}>
                        {es}
                      </span>
                    ))}
                    {p.bereinigtAt && <span className={styles.bereinigtChip}>bereinigt</span>}
                  </div>
                  <div className={styles.infoLine}>
                    <span className={styles.infoLabel}>Bisher:</span>
                    <span className={styles.infoValue}>
                      {[p.currentRelationType, p.currentFunction].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </div>
                  <CleanupContactPreview info={p} />
                  {renderInfoExpander(p.personId)}
                  {renderExistsQ(fr?.exists ?? null, (v) => setF(p.relationId, { exists: v }), p.statusLabel === "Kunde")}
                  {fr?.exists === true &&
                    renderRoleQuestions(p.relationId, p.personId, firmaDetail.firma.id, true)}
                </div>
              );
            })}

            {/* #4: Kontakt (Person) als „Mensch fürs Unternehmen" hinzufügen */}
            <div className={styles.personCard}>
              <span className={styles.qLabel}>
                Kontakt hinzufügen (Mensch fürs Unternehmen)
              </span>
              <span className={styles.qHint}>
                Nur Personen. Beim Speichern wird die Person als „Mensch fürs
                Unternehmen" mit der gewählten Verantwortlichkeit verknüpft.
              </span>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-end",
                  flexWrap: "wrap",
                  marginTop: 6,
                }}
              >
                <div style={{ flex: 1, minWidth: 240 }}>
                  <ContactRefField
                    wantType="Person"
                    personOnly
                    idBase="ap-add"
                    value={addDraft}
                    onChange={setAddDraft}
                  />
                </div>
                <button
                  type="button"
                  className={styles.btn}
                  disabled={
                    !addDraft ||
                    !(addDraft.contactId || addDraft.lastName?.trim())
                  }
                  onClick={() => {
                    if (!addDraft) return;
                    setAdded((prev) => [
                      ...prev,
                      {
                        key: ++addKeyRef.current,
                        ref: addDraft,
                        verant: blankVerant(),
                      },
                    ]);
                    setAddDraft(null);
                  }}
                >
                  + hinzufügen
                </button>
              </div>
              {added.map((a) => (
                <div
                  key={a.key}
                  className={styles.subForm}
                  style={{ marginTop: 8 }}
                >
                  <div className={styles.personHeadRow}>
                    <span className={styles.personName}>
                      {a.ref.displayName || a.ref.lastName || "Neue Person"}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setAdded((prev) => prev.filter((x) => x.key !== a.key))
                      }
                      style={{
                        marginLeft: "auto",
                        border: "none",
                        background: "transparent",
                        color: "#b3261e",
                        cursor: "pointer",
                        fontSize: "0.82rem",
                      }}
                    >
                      entfernen
                    </button>
                  </div>
                  <span className={styles.qLabel}>
                    Verantwortlichkeit (Mensch fürs Unternehmen)
                  </span>
                  <VerantwortlichkeitInput
                    idBase={`ap-add-${a.key}`}
                    value={a.verant}
                    onChange={(v) =>
                      setAdded((prev) =>
                        prev.map((x) =>
                          x.key === a.key ? { ...x, verant: v } : x,
                        ),
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </>
        ) : personDetail ? (
          <>
            {(() => {
              const pnm = personName(
                personDetail.person.lastname,
                personDetail.person.firstName,
                personDetail.person.id,
              );
              return (
                <div className={styles.detailHead}>
                  <div className={styles.personHeadRow}>
                    <span className={styles.detailTitle}>{pnm}</span>
                    <span className={styles.statusInlineLabel}>Kontaktstatus:</span>
                    <span className={styles.statusChip} style={statusChipStyle(personDetail.person.statusLabel)}>
                      {personDetail.person.statusLabel ?? "ohne Status"}
                    </span>
                    {(personDetail.person.extraStatuses ?? []).map((es) => (
                      <span key={es} className={styles.statusChip} style={statusChipStyle(es)}>
                        {es}
                      </span>
                    ))}
                  </div>
                  <CleanupContactPreview info={personDetail.person} />
                  {renderInfoExpander(personDetail.person.id)}
                  {renderExistsQ(personExists, setPersonExists, personDetail.person.statusLabel === "Kunde")}
                </div>
              );
            })()}

            {personExists === true &&
              personDetail.companies.map((c: CleanupCompanyRef) => (
                <div key={c.relationId} className={styles.personCard}>
                  <div className={styles.personHeadRow}>
                    <span className={styles.personName}>{c.companyName ?? `#${c.companyId}`}</span>
                    {c.bereinigtAt && <span className={styles.bereinigtChip}>bereinigt</span>}
                  </div>
                  <div className={styles.infoLine}>
                    <span className={styles.infoLabel}>Bisher:</span>
                    <span className={styles.infoValue}>
                      {[c.currentRelationType, c.currentFunction].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </div>
                  {renderRoleQuestions(c.relationId, personDetail.person.id, c.companyId, false)}
                </div>
              ))}
          </>
        ) : (
          <p className={styles.empty}>Keine Daten.</p>
        )}

        {saveError && (
          <div className={styles.errorBox}>
            <Warning20Regular /> {saveError}
          </div>
        )}
        {saveDone && (
          <div className={styles.successBox}>
            <CheckmarkCircle20Regular /> Gespeichert.
          </div>
        )}

        {!detailLoading && (firmaDetail || personDetail) && (
          <div className={styles.footer}>
            <button
              className={styles.btn}
              onClick={discardDraft}
              disabled={saving}
              title="Eingaben auf den gespeicherten Stand zurücksetzen"
            >
              Verwerfen
            </button>
            <button className={styles.btn} onClick={closeDetail} disabled={saving}>
              <Dismiss20Regular /> Abbrechen
            </button>
            <button
              className={mergeClasses(styles.btn, styles.btnPrimary)}
              onClick={onSave}
              disabled={saving || saveDone}
            >
              <Save20Regular /> {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // --- List render ---
  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zur Datenbereinigung
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <PersonBoard20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Ansprechpartner bereinigen</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Status und Rollen der Firmen-Kontaktpersonen prüfen und korrigieren.
        </span>
      </div>

      <div className={styles.searchRow}>
        <Search20Regular style={{ color: BRAND.muted }} />
        <input
          className={styles.searchInput}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Suche nach Firma oder Person…"
        />
      </div>

      <div className={styles.filterBar}>
        <Filter20Regular style={{ color: BRAND.muted }} />
        <button
          type="button"
          className={mergeClasses(styles.pill, (status === "open") && styles.pillActive)}
          onClick={() => setStatus("open")}
        >
          Offen
        </button>
        <button
          type="button"
          className={mergeClasses(styles.pill, (status === "bereinigt") && styles.pillActive)}
          onClick={() => setStatus("bereinigt")}
        >
          Bereinigt
        </button>
        <span className={styles.spacer} />
        <button
          type="button"
          className={mergeClasses(styles.pill, (mode === "by-firma") && styles.pillActive)}
          onClick={() => setMode("by-firma")}
        >
          <Building20Regular style={{ fontSize: 14, verticalAlign: "-2px", marginRight: 4 }} />
          Nach Firmen
        </button>
        <button
          type="button"
          className={mergeClasses(styles.pill, (mode === "by-person") && styles.pillActive)}
          onClick={() => setMode("by-person")}
        >
          <Person20Regular style={{ fontSize: 14, verticalAlign: "-2px", marginRight: 4 }} />
          Nach Personen
        </button>
        {teams.length > 0 && (
          <select
            className={styles.select}
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            title="Nach Verantwortlichkeit (BO-Team) filtern"
          >
            <option value="">Alle</option>
            <option value="none">Ohne Verantwortlichkeit</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name ?? t.id}
              </option>
            ))}
          </select>
        )}
        <span style={{ color: BRAND.muted, fontSize: "0.8rem", marginLeft: 8 }}>
          {total} {mode === "by-firma" ? "Firmen" : "Personen"}
        </span>
      </div>

      {error && (
        <div className={styles.errorBox}>
          <Warning20Regular /> {error}
        </div>
      )}

      <div id="scrollableDivCleanup" style={{ height: "calc(100vh - 340px)", overflowY: "auto" }}>
        {loading ? (
          <p className={styles.empty}>Lade…</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            {qDebounced ? "Keine Treffer." : "Nichts in dieser Ansicht."}
          </p>
        ) : (
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={<div style={{ textAlign: "center", color: BRAND.muted }}>Lade weitere…</div>}
            scrollableTarget="scrollableDivCleanup"
            style={{ overflow: "visible" }}
          >
            {mode === "by-firma"
              ? rows.map((r) => (
                  <button key={r.companyId} className={styles.card} onClick={() => openDetail(r.companyId)}>
                    <span className={styles.cardIcon}>
                      <Building20Regular style={{ fontSize: 20 }} />
                    </span>
                    <span className={styles.cardName}>{r.companyName ?? `#${r.companyId}`}</span>
                    {r.verantwortlich && (
                      <span className={styles.respChip} title={`Verantwortlich: ${r.verantwortlich}`}>
                        <PersonBoard20Regular style={{ fontSize: 15 }} />
                        {r.verantwortlich}
                      </span>
                    )}
                    <span className={styles.statusChip} style={statusChipStyle(r.statusLabel)}>
                      {statusLabelText(r.statusLabel)}
                    </span>
                    <span className={styles.countBadge}>{r.relationCount} Personen</span>
                    <ChevronRight20Regular style={{ color: BRAND.muted }} />
                  </button>
                ))
              : rows.map((r) => {
                  const names: string[] = Array.isArray(r.companyNames) ? r.companyNames : [];
                  const firmenText =
                    names.length > 0
                      ? names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3} weitere` : "")
                      : `${r.companyCount} Firmen`;
                  return (
                    <button key={r.personId} className={styles.card} onClick={() => openDetail(r.personId)}>
                      <span className={styles.cardIcon}>
                        <Person20Regular style={{ fontSize: 20 }} />
                      </span>
                      <span className={styles.cardCol}>
                        <span className={styles.cardName}>
                          {personName(r.lastname, r.firstName, r.personId)}
                        </span>
                        <span className={styles.cardFirms} title={names.join(", ")}>{firmenText}</span>
                      </span>
                      {r.verantwortlich && (
                        <span className={styles.respChip} title={`Verantwortlich: ${r.verantwortlich}`}>
                          <PersonBoard20Regular style={{ fontSize: 15 }} />
                          {r.verantwortlich}
                        </span>
                      )}
                      <ChevronRight20Regular style={{ color: BRAND.muted }} />
                    </button>
                  );
                })}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default AnsprechpartnerCleanupPage;
